import { useEffect, useState } from "react"

import { Download, ExternalLink, Loader2, X } from "lucide-react"

import { PdfViewer } from "@/components/preview/PdfViewer"
import { SummaryTab } from "@/components/preview/SummaryTab"
import { Button } from "@/components/ui/button"
import { getPreviewUrl } from "@/lib/api"
import { toDownloadUrl } from "@/lib/sharepoint"
import { cn } from "@/lib/utils"
import { usePreview, type PreviewDoc } from "@/providers/PreviewProvider"

function PreviewContent({ doc, onClose }: { doc: PreviewDoc; onClose: () => void }) {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null)
  const [pdfUnavailable, setPdfUnavailable] = useState(false)
  const [noInlinePreview, setNoInlinePreview] = useState(false)
  const [tab, setTab] = useState<"preview" | "summary">("preview")
  const [summaryStarted, setSummaryStarted] = useState(false)

  // Only fetch the iframe-embed URL once the native PDF viewer has reported
  // it can't handle this document (unsupported type, Graph conversion failure).
  useEffect(() => {
    if (!pdfUnavailable) return
    let cancelled = false
    getPreviewUrl({ sourceUrl: doc.url, driveId: doc.driveId, itemId: doc.itemId })
      .then((url) => {
        // The backend echoes the source URL when Graph could not resolve the file; SharePoint
        // refuses to be framed for such URLs, so only a real Office embed URL is shown inline.
        if (cancelled) return
        if (url && url !== doc.url) setEmbedUrl(url)
        else setNoInlinePreview(true)
      })
      .catch(() => {
        if (!cancelled) setNoInlinePreview(true)
      })
    return () => {
      cancelled = true
    }
  }, [doc, pdfUnavailable])

  const switchTab = (next: "preview" | "summary") => {
    setTab(next)
    if (next === "summary") setSummaryStarted(true)
  }

  return (
    <>
      <div className="flex h-12 shrink-0 items-center gap-1 border-b bg-muted/40 px-3">
        <span className="flex-1 truncate text-[13px] font-semibold" title={doc.title}>
          {doc.title}
        </span>
        <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-primary" asChild>
          <a href={toDownloadUrl(doc.url)} target="_blank" rel="noopener noreferrer" title="Download file">
            <Download className="size-3.5" />
          </a>
        </Button>
        <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-primary" asChild>
          <a href={doc.url} target="_blank" rel="noopener noreferrer" title="Open in new tab">
            <ExternalLink className="size-3.5" />
          </a>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground hover:text-destructive"
          onClick={onClose}
          aria-label="Close preview"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex shrink-0 border-b bg-muted/40 px-2">
        {(["preview", "summary"] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => switchTab(key)}
            className={cn(
              "border-b-2 px-4 py-2 text-xs font-medium transition-colors",
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {key === "preview" ? "Preview" : "AI Summary"}
          </button>
        ))}
      </div>

      {/* The preview stays mounted across tab switches so the document doesn't reload */}
      <div className={cn("relative flex-1", tab !== "preview" && "hidden")}>
        {!pdfUnavailable ? (
          <PdfViewer doc={doc} onUnavailable={() => setPdfUnavailable(true)} />
        ) : embedUrl ? (
          <iframe
            src={embedUrl}
            title={doc.title}
            allowFullScreen
            className="absolute inset-0 size-full border-0"
          />
        ) : noInlinePreview ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
            This document cannot be previewed here.
            <Button asChild size="sm">
              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                Open in new tab
              </a>
            </Button>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading preview…
          </div>
        )}
      </div>
      <div className={cn("flex-1 overflow-y-auto", tab !== "summary" && "hidden")}>
        {summaryStarted && <SummaryTab doc={doc} />}
      </div>
    </>
  )
}

export function PreviewPanel() {
  const { doc, closePreview } = usePreview()

  return (
    <aside
      className={cn(
        "flex shrink-0 flex-col overflow-hidden bg-card transition-[width] duration-300 ease-out",
        doc ? "w-[45%] min-w-90 border-l" : "w-0",
      )}
    >
      {doc && <PreviewContent key={`${doc.url}|${doc.itemId ?? ""}`} doc={doc} onClose={closePreview} />}
    </aside>
  )
}
